
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, X } from 'lucide-react';

export interface MarketplaceListing {
  id: string;
  business_name: string;
  description: string;
  contact_phone: string;
  state: string;
  local_government: string;
  approved: boolean;
  created_at: string;
}

interface MarketplaceTableProps {
  listings: MarketplaceListing[];
  loading: boolean;
  isAdmin: boolean;
  onToggleApproval: (id: string, currentStatus: boolean) => void;
}

const MarketplaceTable = ({ 
  listings, 
  loading, 
  isAdmin, 
  onToggleApproval 
}: MarketplaceTableProps) => {
  if (loading) {
    return <div className="text-center py-4 text-gray-800">Loading marketplace listings...</div>;
  }
  
  const visibleListings = listings.filter(listing => listing.approved || isAdmin);
  
  if (visibleListings.length === 0) {
    return (
      <div className="text-center py-4 text-gray-600">
        No marketplace listings available
      </div>
    );
  }
  
  return (
    <div className="rounded-md border overflow-hidden bg-white">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-gray-900 font-semibold">Business</TableHead>
            <TableHead className="hidden md:table-cell text-gray-900 font-semibold">Description</TableHead>
            <TableHead className="text-gray-900 font-semibold">Location</TableHead>
            <TableHead className="text-gray-900 font-semibold">Status</TableHead>
            {isAdmin && <TableHead className="text-gray-900 font-semibold">Action</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleListings.map((listing) => (
            <TableRow key={listing.id} className="hover:bg-gray-50">
              <TableCell>
                <div>
                  <p className="font-medium text-gray-900">{listing.business_name}</p>
                  <p className="text-sm text-gray-700">{listing.contact_phone}</p>
                </div>
              </TableCell>
              <TableCell className="hidden md:table-cell">
                <p className="text-sm text-gray-800 line-clamp-2">{listing.description}</p>
              </TableCell>
              <TableCell>
                <p className="text-sm text-gray-800">{listing.local_government}, {listing.state}</p>
              </TableCell>
              <TableCell>
                {listing.approved ? (
                  <Badge variant="outline" className="bg-green-50 text-green-800 border-green-200 font-medium">
                    Approved
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-800 border-yellow-200 font-medium">
                    Pending
                  </Badge>
                )}
              </TableCell>
              {isAdmin && (
                <TableCell>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => onToggleApproval(listing.id, listing.approved)}
                  >
                    {listing.approved ? (
                      <X size={16} className="text-red-500" />
                    ) : (
                      <Check size={16} className="text-green-500" />
                    )}
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default MarketplaceTable;
